import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import OutboundOutlinedIcon from '@mui/icons-material/OutboundOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import { apiClient } from '@/api/axiosClient';
import { useAuth } from '@/contexts/AuthContext';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { ROLES } from '@/types/auth';
import ToggleSettingCard from './ToggleSettingCard';
import { DATE_FORMAT_OPTIONS } from '@/types/configuration';
import type { ProcessConfigurationDto } from '@/types/configuration';
import type { BranchDto, UpdateBranchRequest } from '@/types/organization';

type SectionAccent = 'primary' | 'secondary' | 'warning' | 'error';

// A colored left-border + icon header per section — reuses the same accent palette already
// established for the sidebar nav groups (AppShell.tsx) so the settings screen reads as part of
// the same design language, whichever page (self-service or SuperAdmin per-company) hosts it.
function SectionCard({
  icon,
  accent,
  title,
  headerAction,
  children,
}: {
  icon: ReactNode;
  accent: SectionAccent;
  title: string;
  // A control (e.g. the section's own master enable/disable Switch) shown at the far right of the
  // header row, level with the title — used by the Production section so its one master switch
  // reads as governing the whole section rather than living inside a single card underneath it.
  headerAction?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ borderLeft: 4, borderColor: `${accent}.main`, p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ display: 'flex', color: `${accent}.main` }}>{icon}</Box>
          <Typography variant="h5" fontWeight={700}>{title}</Typography>
        </Stack>
        {headerAction}
      </Stack>
      {children}
    </Paper>
  );
}

interface ProcessConfigurationFormProps {
  form: ProcessConfigurationDto;
  set: <K extends keyof ProcessConfigurationDto>(key: K, value: ProcessConfigurationDto[K]) => void;
}

/** The full settings form body — shared by the self-service ProcessConfigurationPage (current
 * user's own company) and the SuperAdmin CompanyProcessConfigurationPage (an arbitrary company
 * picked from the Companies console), so the two never drift out of sync with each other. */
export default function ProcessConfigurationForm({ form, set }: ProcessConfigurationFormProps) {
  const { t } = useTranslation('processConfiguration');
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN);

  return (
    <Stack spacing={4}>
      <SectionCard icon={<MoveToInboxOutlinedIcon />} accent="primary" title={t('sections.inbound')}>
        <GridContainer spacing={2}>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.purchaseRequestEnabled')}
              hint={t('fields.purchaseRequestHint')}
              checked={form.purchaseRequestEnabled}
              onChange={(v) => set('purchaseRequestEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.purchaseOrderEnabled')}
              hint={t('fields.purchaseOrderHint')}
              checked={form.purchaseOrderEnabled}
              onChange={(v) => set('purchaseOrderEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.gateEntryEnabled')}
              hint={t('fields.gateEntryHint')}
              checked={form.gateEntryEnabled}
              onChange={(v) => set('gateEntryEnabled', v)}
            />
          </GridItem>
        </GridContainer>
      </SectionCard>

      <SectionCard icon={<FactCheckOutlinedIcon />} accent="secondary" title={t('sections.quality')}>
        <GridContainer spacing={2}>
          <GridItem xs={12} md={4}>
            <ToggleSettingCard
              label={t('fields.qcEnabled')}
              hint={t('fields.qcHint')}
              checked={form.qcEnabled}
              onChange={(v) => set('qcEnabled', v)}
            >
              <Stack direction="row" spacing={3} sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'primary.light' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.qcCheckpointsEnabled}
                      onChange={(e) => set('qcCheckpointsEnabled', e.target.checked)}
                      disabled={!form.qcEnabled}
                    />
                  }
                  label={t('fields.qcCheckpointsEnabled')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.qcCheckpointsMandatory}
                      onChange={(e) => set('qcCheckpointsMandatory', e.target.checked)}
                      disabled={!form.qcEnabled || !form.qcCheckpointsEnabled}
                    />
                  }
                  label={t('fields.qcCheckpointsMandatory')}
                />
              </Stack>
            </ToggleSettingCard>
          </GridItem>
          <GridItem xs={12} md={4}>
            <ToggleSettingCard
              label={t('fields.putawayEnabled')}
              hint={t('fields.putawayHint')}
              checked={form.putawayEnabled}
              onChange={(v) => set('putawayEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', opacity: form.barcodeMandatory ? 1 : 0.6 }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.grnLabelPrintTiming')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {/* Label printing (GRN, Production, and Packing alike) is no longer an independent
                    manual choice — it's required exactly when Barcode Required is on, and hidden
                    everywhere otherwise (see AppShell.tsx's nav gates). */}
                {t('fields.grnLabelPrintTimingHint')}
              </Typography>
              <RadioGroup
                value={form.grnLabelPrintTiming}
                onChange={(e) => set('grnLabelPrintTiming', e.target.value as ProcessConfigurationDto['grnLabelPrintTiming'])}
              >
                <FormControlLabel
                  value="BeforeQc"
                  disabled={!form.barcodeMandatory}
                  control={<Radio />}
                  label={t('grnLabelPrintTimings.BeforeQc')}
                />
                <FormControlLabel
                  value="AfterQc"
                  disabled={!form.barcodeMandatory}
                  control={<Radio />}
                  label={t('grnLabelPrintTimings.AfterQc')}
                />
              </RadioGroup>
            </Paper>
          </GridItem>
          <GridItem xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.stockUpdateTrigger')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('fields.stockUpdateTriggerHint')}
              </Typography>
              <RadioGroup
                value={form.stockUpdateTrigger}
                onChange={(e) => set('stockUpdateTrigger', e.target.value as ProcessConfigurationDto['stockUpdateTrigger'])}
              >
                <FormControlLabel value="OnGrn" control={<Radio />} label={t('stockUpdateTriggers.OnGrn')} />
                <FormControlLabel value="OnQc" control={<Radio />} label={t('stockUpdateTriggers.OnQc')} />
                <FormControlLabel value="OnPutaway" control={<Radio />} label={t('stockUpdateTriggers.OnPutaway')} />
              </RadioGroup>
            </Paper>
          </GridItem>
        </GridContainer>
      </SectionCard>

      <SectionCard
        icon={<PrecisionManufacturingOutlinedIcon />}
        accent="primary"
        title={t('sections.production')}
        // The one common master for the WHOLE Production module — Production Plan, Material
        // Request, Production Receipt, Production Pick List, and the BOM master tab all require
        // this to be on (see AppShell.tsx/MastersPage.tsx's 'productionModuleEnabled' configKey),
        // regardless of their own individual flags below. Off hides every one of them at once.
        headerAction={<Switch checked={form.productionModuleEnabled} onChange={(e) => set('productionModuleEnabled', e.target.checked)} />}
      >
        <GridContainer spacing={2}>
          <GridItem xs={12} md={4}>
            {/* Own switch lives on this card, same placement/layout as the Material Request card
                right next to it — controls the Production Plan sub-flow specifically: Production
                Plan, Production Pick List, and Material Issue screens in the left nav, plus the
                BOM master tab. Independent of Material Request (its own card, not gated by this
                one) — a company can run either, both, or neither — but neither is reachable at
                all unless the section's own common master switch above is also on. */}
            <ToggleSettingCard
              label={t('fields.productionEnabled')}
              hint={t('fields.productionEnabledHint')}
              checked={form.productionEnabled}
              onChange={(v) => set('productionEnabled', v)}
              disabled={!form.productionModuleEnabled}
            >
              <Stack spacing={0.5} sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'primary.light' }}>
                <Typography variant="body2" fontWeight={600}>
                  {t('fields.productionPlanModes')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('fields.productionPlanModesHint')}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.productionPlanBomEnabled}
                      onChange={(e) => set('productionPlanBomEnabled', e.target.checked)}
                      disabled={!form.productionEnabled || !form.productionModuleEnabled}
                    />
                  }
                  label={t('fields.productionPlanBomEnabled')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.productionPlanManualEnabled}
                      onChange={(e) => set('productionPlanManualEnabled', e.target.checked)}
                      disabled={!form.productionEnabled || !form.productionModuleEnabled}
                    />
                  }
                  label={t('fields.productionPlanManualEnabled')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.productionPlanExcelImportEnabled}
                      onChange={(e) => set('productionPlanExcelImportEnabled', e.target.checked)}
                      disabled={!form.productionEnabled || !form.productionModuleEnabled}
                    />
                  }
                  label={t('fields.productionPlanExcelImportEnabled')}
                />
                {!form.productionPlanBomEnabled && !form.productionPlanManualEnabled && !form.productionPlanExcelImportEnabled && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    {t('fields.productionPlanModesNoneWarning')}
                  </Alert>
                )}
              </Stack>
            </ToggleSettingCard>
          </GridItem>
          <GridItem xs={12} md={4}>
            {/* Fully independent of Production Plan — its own switch, not gated by
                form.productionEnabled at all. A company can run Material Request without
                Production Plan being on, or vice versa, or both — but, like Production Plan, only
                once the section's own common master switch above is on. */}
            <ToggleSettingCard
              label={t('fields.materialRequestEnabled')}
              hint={t('fields.materialRequestEnabledHint')}
              checked={form.materialRequestEnabled}
              onChange={(v) => set('materialRequestEnabled', v)}
              disabled={!form.productionModuleEnabled}
            >
              <Stack spacing={0.5} sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'primary.light' }}>
                <Typography variant="body2" fontWeight={600}>
                  {t('fields.materialRequestModes')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('fields.materialRequestModesHint')}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.materialRequestManualEnabled}
                        onChange={(e) => set('materialRequestManualEnabled', e.target.checked)}
                        disabled={!form.materialRequestEnabled || !form.productionModuleEnabled}
                      />
                    }
                    label={t('fields.materialRequestManualEnabled')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.materialRequestExcelImportEnabled}
                        onChange={(e) => set('materialRequestExcelImportEnabled', e.target.checked)}
                        disabled={!form.materialRequestEnabled || !form.productionModuleEnabled}
                      />
                    }
                    label={t('fields.materialRequestExcelImportEnabled')}
                  />
                </Stack>
                {!form.materialRequestManualEnabled && !form.materialRequestExcelImportEnabled && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    {t('fields.materialRequestModesNoneWarning')}
                  </Alert>
                )}
              </Stack>
            </ToggleSettingCard>
          </GridItem>
          <GridItem xs={12} md={4}>
            {/* Third peer card alongside Production Plan and Material Request — Production Receipt
                (and its downstream Production Label Print/Production Putaway screens) is common to
                BOTH, not owned by either: it's independent of both their own toggles, only
                requiring the section's common productionModuleEnabled master. A company running
                only Material Request (no Production Plan at all) can still switch this on. */}
            <ToggleSettingCard
              label={t('fields.productionReceiptEnabled')}
              hint={t('fields.productionReceiptEnabledHint')}
              checked={form.productionReceiptEnabled}
              onChange={(v) => set('productionReceiptEnabled', v)}
              disabled={!form.productionModuleEnabled}
            />
          </GridItem>
          <GridItem xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', opacity: form.productionEnabled && form.productionModuleEnabled ? 1 : 0.6 }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.productionMaterialIssueMode')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('fields.productionMaterialIssueModeHint')}
              </Typography>
              <RadioGroup
                row
                value={form.productionMaterialIssueMode}
                onChange={(e) => set('productionMaterialIssueMode', e.target.value as ProcessConfigurationDto['productionMaterialIssueMode'])}
              >
                <FormControlLabel value="PickList" control={<Radio />} label={t('productionMaterialIssueModes.PickList')} disabled={!form.productionEnabled || !form.productionModuleEnabled} />
                <FormControlLabel value="BatchSelect" control={<Radio />} label={t('productionMaterialIssueModes.BatchSelect')} disabled={!form.productionEnabled || !form.productionModuleEnabled} />
                <FormControlLabel value="ValidationOnly" control={<Radio />} label={t('productionMaterialIssueModes.ValidationOnly')} disabled={!form.productionEnabled || !form.productionModuleEnabled} />
              </RadioGroup>
            </Paper>
          </GridItem>
          <GridItem xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', opacity: form.productionEnabled && form.productionModuleEnabled ? 1 : 0.6 }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.productionPickListGenerationMode')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('fields.productionPickListGenerationModeHint')}
              </Typography>
              <RadioGroup
                row
                value={form.productionPickListGenerationMode}
                onChange={(e) => set('productionPickListGenerationMode', e.target.value as ProcessConfigurationDto['productionPickListGenerationMode'])}
              >
                <FormControlLabel value="Auto" control={<Radio />} label={t('productionPickListGenerationModes.Auto')} disabled={!form.productionEnabled || !form.productionModuleEnabled} />
                <FormControlLabel value="Manual" control={<Radio />} label={t('productionPickListGenerationModes.Manual')} disabled={!form.productionEnabled || !form.productionModuleEnabled} />
              </RadioGroup>
            </Paper>
          </GridItem>
        </GridContainer>
      </SectionCard>

      <SectionCard
        icon={<ScaleOutlinedIcon />}
        accent="warning"
        title={t('sections.weightProduction')}
        // The one common master for the WHOLE Weight-Based Production module — Weight Production
        // Plan, Kiosk capture, Label Print and the Hardware Configuration masters all require it
        // (see AppShell.tsx's 'weightProductionEnabled' configKey). Off by default and fully inert
        // when off, same as Job Work above.
        headerAction={<Switch checked={form.weightProductionEnabled} onChange={(e) => set('weightProductionEnabled', e.target.checked)} />}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t('fields.weightProductionProcessExplanation')}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Paper variant="outlined" sx={{ p: 2, opacity: form.weightProductionEnabled ? 1 : 0.6 }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.weightProductionOverProductionPolicy')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('fields.weightProductionOverProductionPolicyHint')}
              </Typography>
              <RadioGroup
                row
                value={form.weightProductionOverProductionPolicy}
                onChange={(e) => set('weightProductionOverProductionPolicy', e.target.value as ProcessConfigurationDto['weightProductionOverProductionPolicy'])}
              >
                <FormControlLabel value="Block" control={<Radio />} label={t('weightProductionOverProductionPolicies.Block')} disabled={!form.weightProductionEnabled} />
                <FormControlLabel value="AllowWithWarning" control={<Radio />} label={t('weightProductionOverProductionPolicies.AllowWithWarning')} disabled={!form.weightProductionEnabled} />
                <FormControlLabel value="RequireApproval" control={<Radio />} label={t('weightProductionOverProductionPolicies.RequireApproval')} disabled={!form.weightProductionEnabled} />
                <FormControlLabel value="Allow" control={<Radio />} label={t('weightProductionOverProductionPolicies.Allow')} disabled={!form.weightProductionEnabled} />
              </RadioGroup>
            </Paper>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <Paper variant="outlined" sx={{ p: 2, opacity: form.weightProductionEnabled ? 1 : 0.6 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('fields.weightProductionRawMaterialHandlingEnabled')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.weightProductionRawMaterialHandlingEnabledHint')}
                  </Typography>
                </Box>
                <Switch
                  checked={form.weightProductionRawMaterialHandlingEnabled}
                  disabled={!form.weightProductionEnabled}
                  onChange={(e) => set('weightProductionRawMaterialHandlingEnabled', e.target.checked)}
                />
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard
        icon={<HandymanOutlinedIcon />}
        accent="warning"
        title={t('sections.jobWork')}
        // The one common master for the WHOLE Job Work module — Job Work Order/Issue/Receipt and
        // the Job Work QC/Label Print/Putaway return-leg screens all require it (see AppShell.tsx's
        // 'jobWorkEnabled' configKey). Off by default and fully inert when off, same as 3PL above.
        headerAction={<Switch checked={form.jobWorkEnabled} onChange={(e) => set('jobWorkEnabled', e.target.checked)} />}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t('fields.jobWorkProcessExplanation')}
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.jobWorkExplanationVendor')}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.jobWorkExplanationSend')}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.jobWorkExplanationReturn')}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.jobWorkExplanationReuse')}
            </Typography>
          </Stack>
          <Box sx={{ mt: 0.5 }}>
            <GridContainer spacing={2}>
              <GridItem xs={12} md={6}>
                <ToggleSettingCard
                  label={t('fields.jobWorkBomEnabled')}
                  hint={t('fields.jobWorkBomEnabledHint')}
                  checked={form.jobWorkBomEnabled}
                  onChange={(v) => set('jobWorkBomEnabled', v)}
                  disabled={!form.jobWorkEnabled}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <ToggleSettingCard
                  label={t('fields.jobWorkManualEnabled')}
                  hint={t('fields.jobWorkManualEnabledHint')}
                  checked={form.jobWorkManualEnabled}
                  onChange={(v) => set('jobWorkManualEnabled', v)}
                  disabled={!form.jobWorkEnabled}
                />
              </GridItem>
            </GridContainer>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard
        icon={<BusinessCenterOutlinedIcon />}
        accent="warning"
        title={t('sections.thirdPartyLogistics')}
        headerAction={<Switch checked={form.thirdPartyLogisticsEnabled} onChange={(e) => set('thirdPartyLogisticsEnabled', e.target.checked)} />}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t('fields.thirdPartyLogisticsProcessExplanation')}
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.thirdPartyLogisticsExplanationClientMaster')}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.thirdPartyLogisticsExplanationMandatory')}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.thirdPartyLogisticsExplanationSegregation')}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {t('fields.thirdPartyLogisticsExplanationHiddenScreens')}
            </Typography>
          </Stack>
          <Box sx={{ mt: 0.5 }}>
            <GridContainer spacing={2}>
              <GridItem xs={12} md={6}>
                <ToggleSettingCard
                  label={t('fields.billingEnabled')}
                  hint={t('fields.billingEnabledHint')}
                  checked={form.billingEnabled}
                  onChange={(v) => set('billingEnabled', v)}
                  disabled={!form.thirdPartyLogisticsEnabled}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <ToggleSettingCard
                  label={t('fields.clientPortalEnabled')}
                  hint={t('fields.clientPortalEnabledHint')}
                  checked={form.clientPortalEnabled}
                  onChange={(v) => set('clientPortalEnabled', v)}
                  disabled={!form.thirdPartyLogisticsEnabled}
                />
              </GridItem>
            </GridContainer>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard icon={<BalanceOutlinedIcon />} accent="primary" title={t('sections.adjustments')}>
        <GridContainer spacing={2}>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.stockAdjustmentEnabled')}
              hint={t('fields.stockAdjustmentEnabledHint')}
              checked={form.stockAdjustmentEnabled}
              onChange={(v) => set('stockAdjustmentEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.stockTransferEnabled')}
              hint={t('fields.stockTransferEnabledHint')}
              checked={form.stockTransferEnabled}
              onChange={(v) => set('stockTransferEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.cycleCountEnabled')}
              hint={t('fields.cycleCountEnabledHint')}
              checked={form.cycleCountEnabled}
              onChange={(v) => set('cycleCountEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.returnNoteEnabled')}
              hint={t('fields.returnNoteEnabledHint')}
              checked={form.returnNoteEnabled}
              onChange={(v) => set('returnNoteEnabled', v)}
            />
          </GridItem>
        </GridContainer>
      </SectionCard>

      <SectionCard icon={<OutboundOutlinedIcon />} accent="warning" title={t('sections.outbound')}>
        <GridContainer spacing={2}>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.packingEnabled')}
              hint={t('fields.packingHint')}
              checked={form.packingEnabled}
              onChange={(v) => set('packingEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={3}>
            <ToggleSettingCard
              label={t('fields.gateExitEnabled')}
              hint={t('fields.gateExitHint')}
              checked={form.gateExitEnabled}
              onChange={(v) => set('gateExitEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} md={6}>
            <ToggleSettingCard
              label={t('fields.dispatchEnabled')}
              hint={t('fields.dispatchEnabledHint')}
              checked={form.dispatchEnabled}
              onChange={(v) => set('dispatchEnabled', v)}
            >
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('fields.dispatchPickListStrategy')}
                  </Typography>
                  <RadioGroup
                    value={form.dispatchPickListStrategy}
                    onChange={(e) => set('dispatchPickListStrategy', e.target.value as ProcessConfigurationDto['dispatchPickListStrategy'])}
                  >
                    <FormControlLabel value="BatchWiseFifo" control={<Radio />} label={t('dispatchPickListStrategies.BatchWiseFifo')} disabled={!form.dispatchEnabled} />
                    <FormControlLabel value="ProductionDateFifo" control={<Radio />} label={t('dispatchPickListStrategies.ProductionDateFifo')} disabled={!form.dispatchEnabled} />
                    {/* Opt-in, not default — FEFO only makes sense once shelf life/expiry is actually
                        tracked, which not every company does (see ShelfLifeManagementEnabled). */}
                    <FormControlLabel value="Fefo" control={<Radio />} label={t('dispatchPickListStrategies.Fefo')} disabled={!form.dispatchEnabled} />
                  </RadioGroup>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('fields.dispatchPickListGenerationMode')}
                  </Typography>
                  <RadioGroup
                    row
                    value={form.dispatchPickListGenerationMode}
                    onChange={(e) => set('dispatchPickListGenerationMode', e.target.value as ProcessConfigurationDto['dispatchPickListGenerationMode'])}
                  >
                    <FormControlLabel value="Auto" control={<Radio />} label={t('dispatchPickListGenerationModes.Auto')} disabled={!form.dispatchEnabled} />
                    <FormControlLabel value="Manual" control={<Radio />} label={t('dispatchPickListGenerationModes.Manual')} disabled={!form.dispatchEnabled} />
                  </RadioGroup>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('fields.dispatchMaterialScope')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('fields.dispatchMaterialScopeHint')}
                  </Typography>
                  <RadioGroup
                    row
                    value={form.dispatchMaterialScope}
                    onChange={(e) => set('dispatchMaterialScope', e.target.value as ProcessConfigurationDto['dispatchMaterialScope'])}
                  >
                    <FormControlLabel value="FG" control={<Radio />} label={t('dispatchMaterialScopes.FG')} disabled={!form.dispatchEnabled} />
                    <FormControlLabel value="All" control={<Radio />} label={t('dispatchMaterialScopes.All')} disabled={!form.dispatchEnabled} />
                  </RadioGroup>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('fields.dispatchTypeScope')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('fields.dispatchTypeScopeHint')}
                  </Typography>
                  <RadioGroup
                    row
                    value={form.dispatchTypeScope}
                    onChange={(e) => set('dispatchTypeScope', e.target.value as ProcessConfigurationDto['dispatchTypeScope'])}
                  >
                    <FormControlLabel value="Customer" control={<Radio />} label={t('dispatchTypeScopes.Customer')} disabled={!form.dispatchEnabled} />
                    <FormControlLabel value="Warehouse" control={<Radio />} label={t('dispatchTypeScopes.Warehouse')} disabled={!form.dispatchEnabled} />
                    <FormControlLabel value="Both" control={<Radio />} label={t('dispatchTypeScopes.Both')} disabled={!form.dispatchEnabled} />
                  </RadioGroup>
                </Box>
              </Stack>
            </ToggleSettingCard>
          </GridItem>
        </GridContainer>
      </SectionCard>

      <SectionCard icon={<TuneOutlinedIcon />} accent="error" title={t('sections.strategy')}>
        <GridContainer spacing={2}>
          <GridItem xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <TextField
                select
                fullWidth
                label={t('fields.pickingStrategyDefault')}
                value={form.pickingStrategyDefault}
                onChange={(e) => set('pickingStrategyDefault', e.target.value as ProcessConfigurationDto['pickingStrategyDefault'])}
                sx={{ mb: 3 }}
              >
                {(['Fifo', 'Lifo', 'Fefo', 'Batch', 'GrnDate'] as const).map((v) => (
                  <MenuItem key={v} value={v}>
                    {t(`pickingStrategies.${v}`)}
                  </MenuItem>
                ))}
              </TextField>

              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {t('fields.materialValidationRule')}
              </Typography>
              <RadioGroup
                value={form.materialValidationRule}
                onChange={(e) => set('materialValidationRule', e.target.value as ProcessConfigurationDto['materialValidationRule'])}
              >
                {(['MaterialOnly', 'MaterialBatch', 'MaterialBarcode'] as const).map((v) => (
                  <FormControlLabel key={v} value={v} control={<Radio />} label={t(`validationRules.${v}`)} />
                ))}
              </RadioGroup>

              <Divider sx={{ my: 2 }} />
              <Stack>
                <FormControlLabel
                  control={<Checkbox checked={form.allowPartialPicking} onChange={(e) => set('allowPartialPicking', e.target.checked)} />}
                  label={t('fields.allowPartialPicking')}
                />
                <FormControlLabel
                  control={<Checkbox checked={form.allowBoxPicking} onChange={(e) => set('allowBoxPicking', e.target.checked)} />}
                  label={t('fields.allowBoxPicking')}
                />
                <FormControlLabel
                  control={<Checkbox checked={form.allowLoosePicking} onChange={(e) => set('allowLoosePicking', e.target.checked)} />}
                  label={t('fields.allowLoosePicking')}
                />
              </Stack>
            </Paper>
          </GridItem>

          <GridItem xs={12} md={4}>
            <ToggleSettingCard
              label={t('fields.shelfLifeManagementEnabled')}
              checked={form.shelfLifeManagementEnabled}
              onChange={(v) => set('shelfLifeManagementEnabled', v)}
            >
              <Box sx={{ mt: 2 }}>
                <TextField
                  type="number"
                  size="small"
                  label={t('fields.shelfLifeWarningDays')}
                  value={form.shelfLifeWarningDays}
                  onChange={(e) => set('shelfLifeWarningDays', Number(e.target.value))}
                  disabled={!form.shelfLifeManagementEnabled}
                  sx={{ mb: 1.5, width: '100%' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.blockExpiredStock}
                      onChange={(e) => set('blockExpiredStock', e.target.checked)}
                      disabled={!form.shelfLifeManagementEnabled}
                    />
                  }
                  label={t('fields.blockExpiredStock')}
                />
                {form.shelfLifeManagementEnabled && form.blockExpiredStock ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t('fields.blockExpiredStockHint')}
                  </Alert>
                ) : undefined}
              </Box>
            </ToggleSettingCard>
          </GridItem>

          <GridItem xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={600} sx={{ mb: 1.5 }}>
                Compliance &amp; Workflow
              </Typography>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.documentAutoNumberEnabled}
                      onChange={(e) => set('documentAutoNumberEnabled', e.target.checked)}
                    />
                  }
                  label={t('fields.documentAutoNumberEnabled')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.approvalWorkflowEnabled}
                      onChange={(e) => set('approvalWorkflowEnabled', e.target.checked)}
                    />
                  }
                  label={t('fields.approvalWorkflowEnabled')}
                />
              </Stack>
            </Paper>
          </GridItem>
        </GridContainer>
      </SectionCard>

      <SectionCard icon={<LanguageOutlinedIcon />} accent="secondary" title={t('sections.regional')}>
        <GridContainer spacing={2}>
          <GridItem xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.dateFormat')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('fields.dateFormatHint')}
              </Typography>
              <TextField
                select
                fullWidth
                value={form.dateFormat}
                onChange={(e) => set('dateFormat', e.target.value as ProcessConfigurationDto['dateFormat'])}
              >
                {DATE_FORMAT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.value} ({opt.sample})
                  </MenuItem>
                ))}
              </TextField>
            </Paper>
          </GridItem>
          <GridItem xs={12} md={4}>
            <ToggleSettingCard
              label={t('fields.tvDisplayEnabled')}
              hint={t('fields.tvDisplayEnabledHint')}
              checked={form.tvDisplayEnabled}
              onChange={(v) => set('tvDisplayEnabled', v)}
            />
          </GridItem>
          <GridItem xs={12} md={4}>
            <ToggleSettingCard
              label={t('fields.barcodeMandatory')}
              hint={t('fields.barcodeMandatoryHint')}
              checked={form.barcodeMandatory}
              onChange={(v) => set('barcodeMandatory', v)}
            />
          </GridItem>
          <GridItem xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {t('fields.companyLimits')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {isSuperAdmin ? t('fields.companyLimitsHint') : t('fields.companyLimitsReadOnlyHint')}
              </Typography>
              <TextField
                type="number"
                fullWidth
                label={t('fields.maxBranches')}
                placeholder={t('fields.unlimitedPlaceholder')}
                value={form.maxBranches ?? ''}
                disabled={!isSuperAdmin}
                onChange={(e) => set('maxBranches', e.target.value === '' ? null : Number(e.target.value))}
              />
            </Paper>
          </GridItem>
          <GridItem xs={12}>
            <BranchWarehouseLimitsCard companyId={form.companyId} isSuperAdmin={isSuperAdmin} t={t} />
          </GridItem>
        </GridContainer>
      </SectionCard>
    </Stack>
  );
}

/** Per-branch Max Warehouses editor — a branch's warehouse limit is scoped to that branch, not the
 * company (a company with several branches can allow a different number of warehouses per branch),
 * so it can't live as a single field on ProcessConfigurationDto the way Max Branches does. Fetches
 * and saves independently of the surrounding form's own save flow, since Branch is a different
 * resource than ProcessConfiguration. */
function BranchWarehouseLimitsCard({ companyId, isSuperAdmin, t }: { companyId: string; isSuperAdmin: boolean; t: (key: string) => string }) {
  const queryClient = useQueryClient();
  const { data: branches = [], isLoading } = useQuery({
    queryKey: ['branches', companyId],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/branches', { params: { companyId } })).data,
    enabled: Boolean(companyId),
  });

  const [edits, setEdits] = useState<Record<string, string>>({});

  const saveMutation = useMutation({
    mutationFn: ({ branch, maxWarehouses }: { branch: BranchDto; maxWarehouses: number | null }) => {
      const payload: UpdateBranchRequest = {
        branchCode: branch.branchCode, name: branch.name, address: branch.address,
        managerUserId: branch.managerUserId, status: branch.status,
        stateCode: branch.stateCode, stateName: branch.stateName, maxWarehouses,
      };
      return apiClient.put(`/branches/${branch.id}`, payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['branches', companyId] }),
  });

  if (!companyId || isLoading || branches.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography fontWeight={600} sx={{ mb: 0.5 }}>
        {t('fields.branchWarehouseLimits')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {isSuperAdmin ? t('fields.branchWarehouseLimitsHint') : t('fields.companyLimitsReadOnlyHint')}
      </Typography>
      <Stack spacing={1.5}>
        {branches.map((branch) => {
          const editedValue = edits[branch.id];
          const currentValue = editedValue !== undefined ? editedValue : (branch.maxWarehouses ?? '');
          const isDirty = editedValue !== undefined && editedValue !== String(branch.maxWarehouses ?? '');
          return (
            <Stack key={branch.id} direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                {branch.name}
              </Typography>
              <TextField
                type="number"
                size="small"
                sx={{ width: 160 }}
                placeholder={t('fields.unlimitedPlaceholder')}
                value={currentValue}
                disabled={!isSuperAdmin}
                onChange={(e) => setEdits((prev) => ({ ...prev, [branch.id]: e.target.value }))}
              />
              <IconButton
                size="small"
                disabled={!isSuperAdmin || !isDirty || saveMutation.isPending}
                onClick={() => {
                  const maxWarehouses = editedValue === '' ? null : Number(editedValue);
                  saveMutation.mutate(
                    { branch, maxWarehouses },
                    { onSuccess: () => setEdits((prev) => { const next = { ...prev }; delete next[branch.id]; return next; }) },
                  );
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
